import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/i18n";
import { PasswordInput } from "@/components/PasswordInput";
import { CognitoError } from "@/types/auth";

export function NewPassword() {
  const { t } = useTranslation();
  const { state, confirmNewPassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.status !== "new-password-required") return <Navigate to="/login" replace />;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== repeat) {
      setError(t("auth.passwordMismatch", "Die Passwörter stimmen nicht überein."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmNewPassword(password);
      navigate("/", { replace: true });
    } catch (err) {
      const code = err instanceof CognitoError ? err.code : "generic";
      setError(t(`auth.error.${code}`, t("auth.error.generic", "Anmeldung fehlgeschlagen. Bitte erneut versuchen.")));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <form onSubmit={(event) => void submit(event)} className="w-full max-w-sm space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">{t("auth.newPasswordTitle", "Neues Passwort festlegen")}</h2>
        {error && <p role="alert" className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</p>}
        <PasswordInput
          placeholder={t("auth.newPassword", "Neues Passwort")}
          className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base focus:border-brand-primary focus:outline-2 focus:outline-offset-1 focus:outline-brand-primary"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          disabled={busy}
          required
          minLength={8}
        />
        <PasswordInput
          placeholder={t("auth.newPasswordRepeat", "Passwort wiederholen")}
          className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base focus:border-brand-primary focus:outline-2 focus:outline-offset-1 focus:outline-brand-primary"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          autoComplete="new-password"
          disabled={busy}
          required
          minLength={8}
        />
        <button
          type="submit"
          disabled={busy}
          aria-busy={busy}
          className="tap-target flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green py-3 text-sm font-medium text-brand-ink disabled:opacity-70"
        >
          {busy && <Loader2 size={18} className="animate-spin" aria-hidden />}
          {t("common.save", "Speichern")}
        </button>
      </form>
    </div>
  );
}
